import { isTierUnlockBadgeWindowActive } from '@court-sync/shared';
import { useAuthStore } from '@/store/auth';
import type { TierUnlockLevel } from '@/lib/tierUnlockBadges';
import {
  ACADEMY_SETTINGS_TAB_ENTERPRISE_UNLOCKS,
  ACADEMY_SETTINGS_TAB_PROFESSIONAL_UNLOCKS,
  SETTINGS_TAB_ENTERPRISE_UNLOCKS,
  SETTINGS_TAB_PROFESSIONAL_UNLOCKS,
} from '@/lib/tierUnlockBadges';

type TierUnlockPreferences = {
  professionalUnlockBadgesActivatedAt?: string;
  enterpriseUnlockBadgesActivatedAt?: string;
  /** @deprecated Legacy flag — treated as active when no timestamp is stored. */
  professionalUnlockBadgesActive?: boolean;
  /** @deprecated Legacy flag — treated as active when no timestamp is stored. */
  enterpriseUnlockBadgesActive?: boolean;
};

function readTierUnlockBadgeActive(
  preferences: TierUnlockPreferences | null | undefined,
  unlockTier: TierUnlockLevel,
  nowMs: number = Date.now(),
): boolean {
  if (unlockTier === 'professional') {
    if (isTierUnlockBadgeWindowActive(preferences?.professionalUnlockBadgesActivatedAt, nowMs)) {
      return true;
    }
    return Boolean(
      preferences?.professionalUnlockBadgesActive &&
        !preferences?.professionalUnlockBadgesActivatedAt,
    );
  }

  if (isTierUnlockBadgeWindowActive(preferences?.enterpriseUnlockBadgesActivatedAt, nowMs)) {
    return true;
  }
  return Boolean(
    preferences?.enterpriseUnlockBadgesActive && !preferences?.enterpriseUnlockBadgesActivatedAt,
  );
}

export function useTierUnlockBadgeActive(unlockTier: TierUnlockLevel): boolean {
  const preferences = useAuthStore((s) => s.user?.preferences);
  return readTierUnlockBadgeActive(preferences, unlockTier);
}

export function useShowTierUnlockBadge(unlockTier: TierUnlockLevel | null): boolean {
  const proActive = useTierUnlockBadgeActive('professional');
  const enterpriseActive = useTierUnlockBadgeActive('enterprise');
  if (!unlockTier) return false;
  return unlockTier === 'professional' ? proActive : enterpriseActive;
}

function useDualTierTabUnlockTier(
  tabId: string,
  professionalTabs: Partial<Record<string, true>>,
  enterpriseTabs: Partial<Record<string, true>>,
): TierUnlockLevel | null {
  const enterpriseActive = useTierUnlockBadgeActive('enterprise');
  const proActive = useTierUnlockBadgeActive('professional');
  const inEnterprise = Boolean(enterpriseTabs[tabId]);
  const inProfessional = Boolean(professionalTabs[tabId]);
  if (!inEnterprise && !inProfessional) return null;
  if (inEnterprise && enterpriseActive) return 'enterprise';
  if (inProfessional && proActive) return 'professional';
  return null;
}

export function useSettingsTabUnlockTier(tabId: string): TierUnlockLevel | null {
  return useDualTierTabUnlockTier(
    tabId,
    SETTINGS_TAB_PROFESSIONAL_UNLOCKS,
    SETTINGS_TAB_ENTERPRISE_UNLOCKS,
  );
}

export function useAcademySettingsTabUnlockTier(tabId: string): TierUnlockLevel | null {
  return useDualTierTabUnlockTier(
    tabId,
    ACADEMY_SETTINGS_TAB_PROFESSIONAL_UNLOCKS,
    ACADEMY_SETTINGS_TAB_ENTERPRISE_UNLOCKS,
  );
}

export { readTierUnlockBadgeActive };
